import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, BookOpen, PartyPopper } from "lucide-react";
import AudioButton from "../AudioButton";
import { speak, speakWord } from "../../services/phonicsAudio";
import { sfx } from "../../services/soundfx";

export default function ReaderGame({ level, onFinish }) {
  const story = level.stories[0];
  const [page, setPage] = useState(0);
  const [hl, setHl] = useState(-1);
  const [readPages, setReadPages] = useState(() => new Set());
  const timerRef = useRef(null);

  const sentence = story.pages[page];
  const words = sentence.replace(/[.!?]/g, "").split(" ");

  useEffect(() => {
    const t = setTimeout(readAloud, 400);
    return () => {
      clearTimeout(t);
      clearInterval(timerRef.current);
    };
  }, [page]); // eslint-disable-line

  const readAloud = () => {
    speak(sentence);
    setHl(0);
    let i = 0;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      i += 1;
      if (i >= words.length) {
        clearInterval(timerRef.current);
        setTimeout(() => setHl(-1), 400);
        setReadPages((prev) => new Set(prev).add(page));
      } else {
        setHl(i);
      }
    }, 520);
  };

  const allRead = readPages.size >= story.pages.length;

  const nextPage = () => {
    sfx.tap();
    if (page + 1 < story.pages.length) setPage((p) => p + 1);
    else finish();
  };

  const finish = () => {
    sfx.fanfare();
    onFinish({ stars: 3, accuracy: 1 });
  };

  return (
    <div className="relative flex flex-col items-center px-4 pt-3 pb-8 min-h-full no-select">
      <div className="flex items-center gap-2 mt-2 mb-4 font-display text-xl font-bold text-rose-700">
        <BookOpen className="w-6 h-6" /> {story.title}
      </div>

      <motion.div
        key={page}
        initial={{ rotateY: 40, opacity: 0 }}
        animate={{ rotateY: 0, opacity: 1 }}
        className="w-full max-w-xl rounded-[2rem] bg-white border-[6px] border-rose-200 shadow-2xl p-6 sm:p-8 flex flex-col items-center"
      >
        <div className="text-7xl mb-4">{["🐱", "🎩", "☀️", "🤝", "🐷", "🪏", "🫂", "💗"][page % 8]}</div>

        <p className="font-display text-3xl sm:text-4xl font-black leading-snug text-center text-slate-800 flex flex-wrap justify-center gap-x-3 gap-y-1">
          {words.map((w, i) => (
            <button
              key={i}
              data-testid={`story-word-${i}`}
              onClick={() => { sfx.tap(); speakWord(w); }}
              className="pop-btn rounded-xl px-1 transition-colors"
              style={{ background: hl === i ? "#FEF08A" : "transparent", color: hl === i ? "#92400E" : "#1E293B" }}
            >
              {w}
            </button>
          ))}
        </p>

        <div className="mt-6">
          <AudioButton text={sentence} testId="read-sentence-btn" size={64} label="Read this page" />
        </div>
      </motion.div>

      {/* Page nav */}
      <div className="flex items-center gap-4 mt-6">
        <button
          data-testid="prev-page-btn"
          disabled={page === 0}
          onClick={() => { sfx.tap(); setPage((p) => Math.max(0, p - 1)); }}
          className="pop-btn flex items-center justify-center rounded-full bg-white border-4 border-rose-200 shadow-[0_5px_0_#FDA4AF] active:shadow-none disabled:opacity-40"
          style={{ width: 56, height: 56 }}
        >
          <ArrowLeft strokeWidth={3} className="text-rose-500" />
        </button>

        <div className="flex gap-2">
          {story.pages.map((_, i) => (
            <div key={i} className={`w-3 h-3 rounded-full ${readPages.has(i) ? "bg-green-500" : i === page ? "bg-rose-400 animate-pulse" : "bg-slate-300"}`} />
          ))}
        </div>

        {page + 1 < story.pages.length ? (
          <button
            data-testid="next-page-btn"
            onClick={nextPage}
            className="pop-btn flex items-center justify-center rounded-full bg-rose-400 border-4 border-white shadow-[0_5px_0_#E11D48] active:shadow-none"
            style={{ width: 56, height: 56 }}
          >
            <ArrowRight strokeWidth={3} className="text-white" />
          </button>
        ) : (
          <button
            data-testid="finish-story-btn"
            onClick={finish}
            className="pop-btn flex items-center gap-2 rounded-full bg-green-500 text-white font-display font-bold px-5 h-14 border-4 border-white shadow-[0_5px_0_#15803D] active:shadow-none"
          >
            <PartyPopper strokeWidth={2.5} /> Done!
          </button>
        )}
      </div>

      {allRead && page + 1 < story.pages.length && (
        <p className="mt-3 text-sm font-bold text-green-600">You read every page! 🎉</p>
      )}
    </div>
  );
}
